import { SortType } from "../SortType";
import { ShipmentStatusEnum } from "../Shipments/ShipmentStatus";

export type PurchaseTransactionStatusTabId =
  | "all"
  | "new"
  | "ready"
  | "delayed"
  | "shipped"
  | "delivered"
  | "rto"
  | "failed";

export interface PurchaseTransactionStatusTab {
  id: PurchaseTransactionStatusTabId;
  label: string;
  statusIds: number[];
}

export const PURCHASE_TRANSACTION_STATUS_TABS: PurchaseTransactionStatusTab[] =
  [
    { id: "all", label: "All", statusIds: [] },
    { id: "new", label: "New", statusIds: [ShipmentStatusEnum.NEW] },
    {
      id: "ready",
      label: "Ready To Ship",
      statusIds: [ShipmentStatusEnum.READY_TO_SHIP],
    },
    {
      id: "delayed",
      label: "Delayed Delivery",
      statusIds: [
        ShipmentStatusEnum.SHIPPED,
        ShipmentStatusEnum.OUT_FOR_DELIVERY,
      ],
    },
    {
      id: "shipped",
      label: "Shipped",
      statusIds: [
        ShipmentStatusEnum.SHIPPED,
        ShipmentStatusEnum.OUT_FOR_DELIVERY,
      ],
    },
    {
      id: "delivered",
      label: "Delivered",
      statusIds: [ShipmentStatusEnum.DELIVERED],
    },
    {
      id: "rto",
      label: "RTO",
      statusIds: [
        ShipmentStatusEnum.RETURN_REQUESTED,
        ShipmentStatusEnum.RETURNED,
      ],
    },
    {
      id: "failed",
      label: "Failed/Cancelled",
      statusIds: [
        ShipmentStatusEnum.CANCELLED,
        ShipmentStatusEnum.UNDELIVERED,
        ShipmentStatusEnum.DAMAGED,
      ],
    },
  ];

export const getPurchaseTransactionTabById = (
  tabId?: string | null,
): PurchaseTransactionStatusTab | undefined =>
  PURCHASE_TRANSACTION_STATUS_TABS.find((tab) => tab.id === tabId);

export enum UserPurchaseTransactionType {
  ALL = "ALL",
  WALLET = "WALLET",
  PAYMENT = "PAYMENT",
}

export interface UserPurchaseTransactionApiRow {
  shipment_id: number;
  shipment_status: number;
  store_id: number;
  customer_id: number;
  customer_name: string;
  wallet_amount: number | null;
  transaction_id: number | null;
  user_paid_amount: number | null;
  currency: string | null;
  placed_for_user: string | null;
  placed_for_user_email: string | null;
  placed_by_user: string | null;
  placed_by_user_email: string | null;
  created_date: Date | string;
  last_modified_date: Date | string;
}
export interface PurchaseTransactionListQuery {
  store_id: number;
  customer_id?: number;
  transaction_type: UserPurchaseTransactionType;
  tab_id: PurchaseTransactionStatusTabId;
  shipment_status?: number[];
  search?: string;
  sort_type: SortType;
  start_date?: string;
  end_date?: string;
  page_no: number;
  limit: number;
}

export interface PurchaseTransactionListQueryInput {
  store_id?: number | string;
  customer_id?: number | string;
  transaction_type?: string;
  tab_id?: string;
  shipment_status?: string;
  search?: string;
  sort_type?: string;
  start_date?: string;
  end_date?: string;
  page_no?: number | string;
  limit?: number | string;
  store_name?: string;
}

const MAX_LIMIT = 200;
const DEFAULT_LIMIT = 30;
const DEFAULT_PAGE_NO = 1;
const DEFAULT_SORT_TYPE = SortType.DESC;

const toPositiveInt = (
  value: unknown,
  fallback: number,
  max?: number,
): number => {
  const parsed =
    typeof value === "string" ? parseInt(value, 10) : Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback;
  }
  return max !== undefined ? Math.min(parsed, max) : parsed;
};

const toSortType = (value: unknown): SortType => {
  const upper = typeof value === "string" ? value.toUpperCase() : value;
  return upper === SortType.ASC || upper === SortType.DESC
    ? (upper as SortType)
    : DEFAULT_SORT_TYPE;
};

const toTransactionType = (value: unknown): UserPurchaseTransactionType => {
  const upper = typeof value === "string" ? value.toUpperCase() : value;
  if (upper === UserPurchaseTransactionType.WALLET) {
    return UserPurchaseTransactionType.WALLET;
  }
  if (upper === UserPurchaseTransactionType.PAYMENT) {
    return UserPurchaseTransactionType.PAYMENT;
  }
  return UserPurchaseTransactionType.ALL;
};

const toShipmentStatusIds = (value?: string): number[] | undefined => {
  if (!value?.trim()) return undefined;

  const statusIds = value
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);

  return statusIds.length > 0 ? statusIds : undefined;
};

const resolvePurchaseTransactionTab = (
  input: PurchaseTransactionListQueryInput,
): PurchaseTransactionStatusTab => {
  const tabFromId = getPurchaseTransactionTabById(input.tab_id);
  if (tabFromId) {
    return tabFromId;
  }

  const statusIds = toShipmentStatusIds(input.shipment_status);
  if (statusIds?.length) {
    const tabFromStatus = PURCHASE_TRANSACTION_STATUS_TABS.find(
      (tab) =>
        tab.statusIds.length === statusIds.length &&
        tab.statusIds.every((id) => statusIds.includes(id)),
    );
    if (tabFromStatus) {
      return tabFromStatus;
    }
  }

  return PURCHASE_TRANSACTION_STATUS_TABS[1];
};

export const parsePurchaseTransactionListQuery = (
  input: PurchaseTransactionListQueryInput,
): PurchaseTransactionListQuery => {
  const storeId = toPositiveInt(input.store_id, 0);
  if (!storeId) {
    throw new Error("store_id is required");
  }

  const search = input.search?.trim();
  const customerId = input.customer_id
    ? toPositiveInt(input.customer_id, 0)
    : undefined;
  const selectedTab = resolvePurchaseTransactionTab(input);

  return {
    store_id: storeId,
    ...(customerId ? { customer_id: customerId } : {}),
    transaction_type: toTransactionType(input.transaction_type),
    tab_id: selectedTab.id,
    ...(selectedTab.statusIds.length
      ? { shipment_status: [...selectedTab.statusIds] }
      : {}),
    ...(search ? { search } : {}),
    page_no: toPositiveInt(input.page_no, DEFAULT_PAGE_NO),
    limit: toPositiveInt(input.limit, DEFAULT_LIMIT, MAX_LIMIT),
    sort_type: toSortType(input.sort_type),
    ...(input.start_date ? { start_date: input.start_date } : {}),
    ...(input.end_date ? { end_date: input.end_date } : {}),
  };
};

export const purchaseTransactionQueryToSearchParams = (
  query: PurchaseTransactionListQuery,
  extras?: { store_name?: string },
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("store_id", String(query.store_id));
  if (query.customer_id) {
    params.set("customer_id", String(query.customer_id));
  }
  if (extras?.store_name) {
    params.set("store_name", extras.store_name);
  }
  if (query.search) {
    params.set("search", query.search);
  }
  if (query.transaction_type !== UserPurchaseTransactionType.ALL) {
    params.set("transaction_type", query.transaction_type);
  }
  if (query.tab_id !== "all") {
    params.set("tab_id", query.tab_id);
  }
  if (query.shipment_status?.length) {
    params.set("shipment_status", query.shipment_status.join(","));
  }
  if (query.start_date) {
    params.set("start_date", query.start_date);
  }
  if (query.end_date) {
    params.set("end_date", query.end_date);
  }
  params.set("page_no", String(query.page_no));
  params.set("limit", String(query.limit));
  params.set("sort_type", query.sort_type);
  return params;
};

export const buildDefaultPurchaseTransactionQuery = (
  storeId: number,
  customerId?: number,
): PurchaseTransactionListQuery => ({
  store_id: storeId,
  ...(customerId ? { customer_id: customerId } : {}),
  transaction_type: UserPurchaseTransactionType.ALL,
  tab_id: "all",
  page_no: DEFAULT_PAGE_NO,
  limit: DEFAULT_LIMIT,
  sort_type: DEFAULT_SORT_TYPE,
});

export type PurchaseTransactionDownloadResult =
  | { type: "file"; data: Blob; filename?: string }
  | { type: "email"; message: string };
